import { useAuthHeaders } from '@/composables/useAuthHeaders';
const { getAuthHeaders } = useAuthHeaders();
import axios from 'axios';
import { showError } from '@/composables/useToast';
import { urlLogs } from '@/helpers';

const SESSION_EXPIRED_FRAGMENTS = [
   'Unauthorized',
   'token expired',
   'Token not provided',
   'Usuario no autenticado',
   'No se encontró el token de acceso',
];

const isSessionExpiredResponse = (error) => {
   if (error.response?.status !== 401) {
      return false;
   }

   const message = String(
      error.response?.data?.message ?? error.response?.data?.error ?? '',
   );

   return SESSION_EXPIRED_FRAGMENTS.some((fragment) =>
      message.includes(fragment),
   );
};

const sendErrorLog = async (error, requestConfig = {}) => {
   const { ok, config, message } = getAuthHeaders();


   try {
      let errorType = 'UNKNOWN';
      let errorMessage = error.message || 'Error desconocido';

      if (
         error.code === 'NETWORK_ERROR' ||
         error.message?.includes('Network Error')
      ) {
         errorType = 'NETWORK_ERROR';
         errorMessage = 'Error de red - No se pudo conectar al servidor';
      } else if (
         error.code === 'ECONNABORTED' ||
         error.message?.includes('timeout')
      ) {
         errorType = 'TIMEOUT_ERROR';
         errorMessage = 'Timeout - La petición tardó demasiado en responder';
      } else if (error.response) {
         errorType = 'HTTP_ERROR';
         errorMessage = `Error HTTP ${error.response.status}: ${error.response.statusText}`;
      } else if (error.request) {
         errorType = 'REQUEST_ERROR';
         errorMessage = 'Error en la petición - Sin respuesta del servidor';
      }
      const logData = {
         level: 'error',
         message: errorMessage,
         errorType: errorType,
         url: requestConfig.url || error.config?.url || 'URL no disponible',
         method:
            requestConfig.method ||
            error.config?.method?.toUpperCase() ||
            'MÉTODO no disponible',
         status: error.response?.status || error.status || null,
         timestamp: new Date().toISOString(),
         userAgent: navigator.userAgent,
         stack: error.stack || null,

         responseData:
            error.response?.data?.error ||
            error.response?.data?.message ||
            null,

         duration: error.config?.metadata?.startTime
            ? new Date() - error.config.metadata.startTime
            : null,

         errorCode: error.code || null,
      };

      await axios.post(urlLogs, logData, {
         timeout: 5000,
         headers: config.headers,
      });
   } catch (logError) {
      // console.error('Error al enviar log al backend:', logError);
      // console.error('Error original:', error);
   }
};

axios.interceptors.request.use(
   (config) => {
      config.metadata = {
         startTime: new Date(),
         requestData: config.data || null,
         requestParams: config.params || null,
         requestHeaders: config.headers || null,
      };
      return config;
   },
   (error) => {
      sendErrorLog(error, { method: 'REQUEST_CONFIG_ERROR' });
      return Promise.reject(error);
   },
);

axios.interceptors.response.use(
   (response) => response,
   async (error) => {
      const isLogRequest = error.config?.url?.includes('/logs');

      if (!isLogRequest && error.response?.status !== 401) {
         await sendErrorLog(error);
      } else if (isSessionExpiredResponse(error)) {
         await window.location.reload();
         showError('Sesión expirada, por favor inicie sesión nuevamente.');
         return Promise.reject(new Error('Sesión expirada', 401));
      } else if (error.response?.status === 401) {
         await sendErrorLog(error);
      }

      return Promise.reject(error);
   },
);

export const httpInstance = {
   unauthenticatedGet: async (url, config = {}) => {
      const response = await axios.get(url, config);
      return response;
   },
   unauthenticatedPost: async (url, body) => {
      const response = await axios.post(url, body);
      return response;
   },
   unauthenticatedPut: async (url, body) => {
      const response = await axios.put(url, body);
      return response;
   },

   get: async (url, params, options = {}) => {
      const { ok, config, message } = getAuthHeaders();

      if (!ok) {
         return { ok: false, message };
      }
      const response = await axios.get(url, {
         params,
         headers: config.headers,
         ...options,
      });
      return response;
   },
   post: async (url, body, content = false) => {
      const { ok, config, message } = getAuthHeaders();

      if (!ok) {
         return { ok: false, message };
      }
      const headers = !content
         ? config
         : {
            headers: {
               Authorization: config.headers.Authorization,
               'Content-Type': 'multipart/form-data',
            },
         };
      const response = await axios.post(url, body, headers);
      return response;
   },
   put: async (url, body, content = false) => {
      const { ok, config, message } = getAuthHeaders();

      if (!ok) {
         return { ok: false, message };
      }
      if (content) {
         const response = await axios.put(url, body, {
            headers: {
               Authorization: config.headers.Authorization,
               id_empresa: config.headers.id_empresa,
            },
         });
         return response;
      }
      const response = await axios.put(url, body, config);
      return response;
   },
   patch: async (url, body) => {
      const { ok, config, message } = getAuthHeaders();

      if (!ok) {
         return { ok: false, message };
      }
      const response = await axios.patch(url, body, config);
      return response;
   },
   delete: async (url, configOverride = {}) => {
      const { ok, config, message } = getAuthHeaders();

      if (!ok) {
         return { ok: false, message };
      }
      const response = await axios.delete(url, { ...config, ...configOverride });
      return response;
   },
   getFileEmail: async (url, params = {}, options = {}) => {
      const { ok, config, message } = getAuthHeaders();

      if (!ok) {
         return { ok: false, message };
      }

      const response = await axios.get(url, {
         ...options,
         params,
         headers: {
            ...config.headers,
            ...options.headers,
         },
      });

      return response;
   },
};
